import React, { useState, useCallback } from 'react';
import {
    Box,
    Select,
    MenuItem,
    FormControl,
    CircularProgress,
    Typography
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import { apiHandler } from '../../../../utils/api/responseHandler';
import config from '../../../../config';
import { useComponentVersion } from '../../../../hooks/quoter/useComponentVersion';
import { useTheme } from '@mui/material/styles';

interface LPAdjusterProps {
    quoteId: number;
    initialValue: number;
    onError: (error: string) => void;
    disabled?: boolean;
    minLP?: number;
    maxLP?: number;
    lpStep?: number;
}

//????? Is this setVersion an issue?
const LPAdjuster: React.FC<LPAdjusterProps> = ({
    quoteId,
    disabled = false,
    initialValue,
    minLP = 80,
    maxLP = 160,
    lpStep = 5,
    onError
}) => {
    //const { notify, eventManager } = useQuoteUpdate();
    const [lpValue, setLpValue] = useState<string>(initialValue?.toString() || '');
    const [isUpdating, setIsUpdating] = useState(false);

    const theme = useTheme();

    const lpOptions = React.useMemo(() => {
        const options = [];
        for (let i = minLP; i <= maxLP; i += lpStep) {
            options.push(i);
        }
        return options;
    }, [minLP, maxLP, lpStep]);

    // Use component version hook - only need to request updates, no need to respond to them
    const { requestVersionUpdate } = useComponentVersion('lpAdjuster');

    const handleLPUpdate = useCallback(async (event: SelectChangeEvent<string>) => {
        const newValue = event.target.value;
        const numValue = parseFloat(newValue);

        if (isNaN(numValue)) return;

        setIsUpdating(true);
        setLpValue(newValue);

        try {
            // Make API call to update LP
            const url = `${config.API_URL}/api/quotes/${quoteId}/lp`;
            const response = await apiHandler.fetch(url, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ percentLP: numValue })
            });

            if (response.error) throw response.error;

            // Request a version update using the hook
            requestVersionUpdate();
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to update LP';
            onError?.(errorMessage);
            setLpValue(initialValue?.toString() || '');
        } finally {
            setIsUpdating(false);
        }
    }, [quoteId, initialValue, onError, requestVersionUpdate]);

    return (
        <Box 
            display="flex" 
            alignItems="center" 
            gap={1}
            sx={{
                backgroundColor: 'rgba(25, 118, 210, 0.10)',
                borderRadius: 1,
                px: 1.5,
                py: 0.75,
                border: '1px solid',
                borderColor: 'rgba(25, 118, 210, 0.25)'
            }}
        >
            <Typography 
                variant="body2" 
                fontWeight={500}
                color="text.secondary"
                sx={{ whiteSpace: 'nowrap' }}
            >
                LP %
            </Typography>
            <FormControl size="small" sx={{ minWidth: 105 }}>
                <Select
                    value={lpValue}
                    onChange={handleLPUpdate}
                    disabled={disabled || isUpdating}
                    sx={{
                        height: 30,
                        minHeight: 30,
                        '& .MuiSelect-select': {
                            py: '2px !important',
                            minHeight: 'unset !important',
                            fontSize: '0.875rem'
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                            top: 0
                        }
                    }}
                >
                    {lpOptions.map((value) => (
                        <MenuItem key={value} value={value.toString()} sx={{ fontSize: '0.875rem' }}>
                            {value}%
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
            {isUpdating && <CircularProgress size={16} />}
        </Box>
    );
};

export default LPAdjuster;